import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleById, getArticlesByCategory } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const article = getArticleById(id || '');

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">你访问的文章可能已被删除或不存在</p>
            <Link to="/" className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getArticlesByCategory(article.categoryId)
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <nav className="bg-background-100 border-b border-background-200/70" aria-label="面包屑导航">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-3">
            <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
              <li><Link to="/" className="hover:text-primary-600 transition-colors">首页</Link></li>
              <li className="text-foreground-300">/</li>
              <li><Link to={`/category/${article.categoryId}`} className="hover:text-primary-600 transition-colors">{article.categoryName}</Link></li>
              <li className="text-foreground-300">/</li>
              <li className="text-foreground-900 font-medium truncate max-w-[200px]">{article.title}</li>
            </ol>
          </div>
        </nav>

        <article className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-md text-xs font-medium">
                  {article.categoryName}
                </span>
                {article.featured && (
                  <span className="px-3 py-1 bg-accent-100 text-accent-700 rounded-md text-xs font-medium">
                    精选
                  </span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-5">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <div className="flex items-center gap-2">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <span className="font-medium text-foreground-900">{article.author}</span>
                </div>
                <span className="text-foreground-300">·</span>
                <span>{article.publishDate}</span>
                <span className="text-foreground-300">·</span>
                <span>阅读 {article.readTime}</span>
              </div>
            </header>

            <div className="mb-10 rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full object-cover max-h-[420px]"
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2 mb-6">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 bg-background-100 text-foreground-600 rounded-full text-xs font-medium"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-6 text-sm text-foreground-500">
                <button className="flex items-center gap-2 px-4 py-2 bg-background-100 rounded-lg hover:bg-background-200 transition-colors cursor-pointer">
                  <i className="ri-heart-line text-lg"></i>
                  <span>{article.likes.toLocaleString()} 赞</span>
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-background-100 rounded-lg hover:bg-background-200 transition-colors cursor-pointer">
                  <i className="ri-chat-1-line text-lg"></i>
                  <span>{article.comments} 评论</span>
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-background-100 rounded-lg hover:bg-background-200 transition-colors cursor-pointer">
                  <i className="ri-bookmark-line text-lg"></i>
                  <span>收藏</span>
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-background-100 rounded-lg hover:bg-background-200 transition-colors cursor-pointer">
                  <i className="ri-share-forward-line text-lg"></i>
                  <span>分享</span>
                </button>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden hover:bg-background-200/50 transition-colors cursor-pointer"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading font-semibold text-sm text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed mb-3">
                        {ra.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{ra.author}</span>
                        <span>{ra.publishDate}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}